/**
 * CouncilReviewQueuePanel — real-data Council Review Queue.
 *
 * Source of truth: GET /api/v1/council/queue (read-only backend endpoint
 * that aggregates the on-disk Council decision artifacts and the enriched
 * per-strategy status). Never fabricates Council decisions. Never claims
 * "approved" unless the backend's `review_status` literally says so.
 *
 * Display rules:
 *   - Readable ET timestamps; no raw ISO in the main UI.
 *   - No raw JSON.
 *   - Provider votes shown as 4 chips (GPT/Claude/Gemini/Perplexity).
 *   - Long final_aggregation is collapsed with expand/collapse.
 *   - Evidence requirements rendered as bullets.
 *   - `live_trading_blocked` shown as a calm "Live Blocked" badge,
 *     not an alarmist red banner.
 */
import React, { useMemo, useState } from 'react';
import {
  AlertOctagon,
  Brain,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Clock,
  Database,
  Eye,
  Loader2,
  Lock,
  Shield,
  ShieldCheck,
  Target,
} from 'lucide-react';
import {
  fmtEtTime,
  fmtRelative,
  severityToClass,
  Severity,
  titleize,
} from './formatters';
import { BrainIcon, OwlIcon, ElephantIcon, CompassIcon } from '../AIIcons';
import {
  useCouncilQueue,
  CouncilQueueItem,
  ReviewStatus,
} from '../../hooks/useCouncilQueue';

const PROVIDER_DISPLAY: Array<{ id: string; name: string; role: string }> = [
  { id: 'gpt', name: 'GPT', role: 'Market Analyst' },
  { id: 'claude', name: 'Claude', role: 'Risk Reviewer' },
  { id: 'gemini', name: 'Gemini', role: 'Structural Analyst' },
  { id: 'perplexity', name: 'Perplexity', role: 'Research Lead' },
];

function reviewStatusSeverity(s: string | ReviewStatus | undefined): Severity {
  switch ((s || '').toString().toLowerCase()) {
    case 'approved':
      return 'ok';
    case 'needs_more_data':
      return 'review';
    case 'pending':
      return 'monitoring';
    case 'rejected':
      return 'lock';
    default:
      return 'unknown';
  }
}

function reviewStatusLabel(s: string | ReviewStatus | undefined): string {
  switch ((s || '').toString().toLowerCase()) {
    case 'approved':
      return 'Approved';
    case 'needs_more_data':
      return 'Needs More Data';
    case 'pending':
      return 'Pending';
    case 'rejected':
      return 'Rejected';
    default:
      return 'Status Unknown';
  }
}

function voteSeverity(v: string | null): Severity {
  const t = (v || '').toLowerCase();
  if (!t) return 'unknown';
  if (t.includes('approve_paper') || t === 'approved' || t === 'approve') return 'ok';
  if (t.includes('approve_shadow')) return 'monitoring';
  if (t === 'needs_more_data') return 'review';
  if (t === 'rejected' || t === 'reject' || t === 'blocked') return 'lock';
  return 'monitoring';
}

function voteLabel(v: string | null): string {
  if (!v) return 'No vote';
  return titleize(v);
}

function sourceSeverity(s: string | undefined): Severity {
  switch ((s || '').toUpperCase()) {
    case 'LIVE':
      return 'ok';
    case 'LIMITED':
      return 'limited';
    default:
      return 'unknown';
  }
}

function topStatusSeverity(s: string | undefined): Severity {
  return sourceSeverity(s);
}

// Per-provider branded Council member icons. Falls back to the generic
// lucide Brain glyph if a provider id arrives that we have not mapped.
const PROVIDER_ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
  gpt: BrainIcon,
  claude: OwlIcon,
  gemini: ElephantIcon,
  perplexity: CompassIcon,
};

function ProviderVoteChip({
  pid,
  name,
  role,
  vote,
}: {
  pid: string;
  name: string;
  role: string;
  vote: string | null;
}) {
  const sev = voteSeverity(vote);
  return (
    <div
      data-testid={`council-queue-vote-${pid}`}
      className={`flex flex-col gap-0.5 p-2 rounded-lg border ${severityToClass(sev)}`}
    >
      <div className="text-[10px] font-black uppercase tracking-widest italic flex items-center gap-1">
        {(() => {
          const IconCmp = PROVIDER_ICON_MAP[pid];
          return IconCmp ? <IconCmp size={14} /> : <Brain size={10} />;
        })()}
        {name}
      </div>
      <div className="text-[9px] font-bold opacity-80 uppercase tracking-widest">
        {role}
      </div>
      <div className="text-[11px] font-black italic mt-0.5">
        {voteLabel(vote)}
      </div>
    </div>
  );
}

function QueueItemCard({ item }: { item: CouncilQueueItem }) {
  const [expanded, setExpanded] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const sev = reviewStatusSeverity(item.review_status);
  const conf =
    typeof item.confidence === 'number' && isFinite(item.confidence)
      ? `${Math.round(item.confidence * 100)}%`
      : '—';
  const finalAgg = item.final_aggregation || '';
  const showExpander = finalAgg.length > 320;
  const finalAggBody = showExpander && !expanded ? finalAgg.slice(0, 320) + '…' : finalAgg;

  const ew = item.evidence_window || { days_with_evidence: 0, target_days: 30, event_count: 0 };

  return (
    <li
      data-testid={`council-queue-row-${item.strategy_id}`}
      className="border border-[var(--card-border)] rounded-xl p-3 bg-[var(--input-bg)]/40"
    >
      {/* Header row: strategy + account + review-status badge */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[12px] font-black text-[var(--app-text)] italic truncate flex items-center gap-2">
            <ClipboardList size={12} className="text-sky-600" />
            {item.strategy_name || item.strategy_id}
          </div>
          <div className="text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest truncate">
            {item.strategy_id}
            {item.account_label ? ` · ${item.account_label}` : item.account_id ? ` · ${item.account_id}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            data-testid={`council-queue-status-${item.strategy_id}`}
            className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              sev,
            )}`}
          >
            {reviewStatusLabel(item.review_status)}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              sourceSeverity(item.source_status),
            )}`}
          >
            Source: {(item.source_status || 'UNAVAILABLE').toString().toUpperCase()}
          </span>
        </div>
      </div>

      {/* Council decision + confidence + activation chips */}
      <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg p-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Council Decision
          </div>
          <div className="text-[12px] font-black text-[var(--app-text)] italic">
            {titleize(item.council_decision) || '—'}
          </div>
          <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
            Confidence: {conf}
          </div>
        </div>
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg p-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Activation
          </div>
          <div className="text-[11px] font-black text-[var(--app-text)] italic">
            {titleize(item.activation_status) || '—'}
          </div>
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {item.data_collection_mode ? (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${severityToClass(
                  'monitoring',
                )}`}
              >
                <Database size={9} /> Data Collection
              </span>
            ) : null}
            {item.live_trading_blocked ? (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${severityToClass(
                  'ok',
                )}`}
              >
                <Lock size={9} /> Live Blocked
              </span>
            ) : (
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border ${severityToClass(
                  'limited',
                )}`}
              >
                <ShieldCheck size={9} /> Live Possible
              </span>
            )}
          </div>
        </div>
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg p-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Evidence Window
          </div>
          <div className="text-[11px] font-black text-[var(--app-text)] italic flex items-center gap-1">
            <Target size={11} className="text-emerald-600" />
            Day {ew.days_with_evidence} of {ew.target_days}
          </div>
          <div className="text-[10px] text-[var(--muted-text)] mt-0.5">
            {ew.event_count} event{ew.event_count === 1 ? '' : 's'} this session
          </div>
        </div>
      </div>

      {/* Provider votes */}
      <div className="mt-2">
        <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1">
          Provider Votes
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {PROVIDER_DISPLAY.map(({ id, name, role }) => (
            <ProviderVoteChip
              key={id}
              pid={id}
              name={name}
              role={role}
              vote={
                item.provider_votes && typeof item.provider_votes[id] !== 'undefined'
                  ? item.provider_votes[id]
                  : null
              }
            />
          ))}
        </div>
      </div>

      {/* Final aggregation */}
      {finalAgg && (
        <div className="mt-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1">
            Final Aggregation
          </div>
          <p
            data-testid={`council-queue-aggregation-${item.strategy_id}`}
            className="text-[11px] text-[var(--app-text)] italic leading-relaxed"
          >
            {finalAggBody}
          </p>
          {showExpander && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
              data-testid={`council-queue-agg-toggle-${item.strategy_id}`}
            >
              {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              {expanded ? 'Collapse' : 'Read more'}
            </button>
          )}
        </div>
      )}

      {/* Timestamps */}
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-1 text-[10px] text-[var(--muted-text)]">
        <div className="flex items-center gap-1.5">
          <Clock size={10} />
          <span>
            <span className="font-black uppercase tracking-widest text-[9px] mr-1">
              Last review:
            </span>
            {fmtRelative(item.last_reviewed_at)}{' '}
            <span className="opacity-70">
              ({item.last_reviewed_at ? fmtEtTime(item.last_reviewed_at) : '—'})
            </span>
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Eye size={10} />
          <span>
            <span className="font-black uppercase tracking-widest text-[9px] mr-1">
              Next review due:
            </span>
            {item.next_review_due_at ? fmtEtTime(item.next_review_due_at) : '—'}
          </span>
        </div>
      </div>

      {/* Required evidence */}
      {Array.isArray(item.next_required_evidence) && item.next_required_evidence.length > 0 && (
        <div className="mt-2">
          <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            Next Required Evidence
          </div>
          <ul
            data-testid={`council-queue-evidence-${item.strategy_id}`}
            className="mt-1 space-y-1"
          >
            {item.next_required_evidence.slice(0, 8).map((s, i) => (
              <li
                key={i}
                className="text-[11px] text-[var(--app-text)] italic flex items-start gap-1.5"
              >
                <span className="text-[var(--muted-text)] shrink-0 mt-0.5">•</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Diagnostics (collapsed by default) */}
      <div className="mt-2">
        <button
          type="button"
          onClick={() => setDiagOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
          data-testid={`council-queue-diag-toggle-${item.strategy_id}`}
        >
          {diagOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          Diagnostics
        </button>
        {diagOpen && (
          <div className="mt-1 p-2 rounded border border-[var(--card-border)] bg-[var(--input-bg)]/30">
            <ul className="text-[10px] text-[var(--muted-text)] space-y-1">
              <li>
                <span className="font-black uppercase tracking-widest mr-1">Strategy ID:</span>
                {item.strategy_id}
              </li>
              {item.account_id && (
                <li>
                  <span className="font-black uppercase tracking-widest mr-1">Account:</span>
                  {item.account_label || item.account_id}
                </li>
              )}
              {Array.isArray(item.source_files) && item.source_files.length > 0 && (
                <li>
                  <span className="font-black uppercase tracking-widest mr-1">Sources:</span>
                  {item.source_files.length} file
                  {item.source_files.length === 1 ? '' : 's'}
                </li>
              )}
              <li>
                <span className="font-black uppercase tracking-widest mr-1">Source status:</span>
                {item.source_status}
              </li>
            </ul>
          </div>
        )}
      </div>
    </li>
  );
}

export default function CouncilReviewQueuePanel() {
  const { payload, loading, error } = useCouncilQueue();
  const [topDiagOpen, setTopDiagOpen] = useState(false);

  const items: CouncilQueueItem[] = useMemo(
    () => (payload && Array.isArray(payload.items) ? payload.items : []),
    [payload],
  );

  if (loading) {
    return (
      <section
        data-testid="council-queue-loading"
        className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-5"
      >
        <div className="flex items-center gap-2 text-[var(--muted-text)]">
          <Loader2 size={14} className="animate-spin" />
          <span className="text-[11px] uppercase tracking-widest font-black">
            Loading Council review queue…
          </span>
        </div>
      </section>
    );
  }

  // Unreachable endpoint -> clean fallback.
  if (!payload || (error && !payload)) {
    return (
      <section
        data-testid="council-queue-unavailable"
        className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-5"
      >
        <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
          <Brain size={14} /> Council Review Queue
        </h3>
        <div className="mt-3 text-xs text-[var(--muted-text)]">
          Council review queue is not connected yet.{' '}
          <span
            className={`inline-flex items-center gap-1 ml-2 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              'unknown',
            )}`}
          >
            Source: UNAVAILABLE
          </span>
        </div>
      </section>
    );
  }

  const topSev = topStatusSeverity(payload.status);

  return (
    <section
      data-testid="council-queue"
      className="border border-[var(--card-border)] bg-[var(--card-bg)] rounded-2xl p-5"
    >
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-black uppercase italic text-[var(--app-text)] flex items-center gap-2">
          <Brain size={14} /> Council Review Queue
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">
            {items.length} item{items.length === 1 ? '' : 's'}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-md border ${severityToClass(
              topSev,
            )}`}
            data-testid="council-queue-source"
          >
            Source: {(payload.status || 'UNAVAILABLE').toString().toUpperCase()}
          </span>
        </div>
      </header>

      {items.length === 0 ? (
        <p
          data-testid="council-queue-empty"
          className="mt-3 text-xs text-[var(--muted-text)]"
        >
          No Council review items are currently queued.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {items.map((it) => (
            <QueueItemCard key={`${it.strategy_id}`} item={it} />
          ))}
        </ul>
      )}

      {/* Top-level diagnostics (collapsed by default) */}
      {payload.diagnostics_raw && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setTopDiagOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] hover:text-[var(--app-text)]"
          >
            {topDiagOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
            Queue Diagnostics
          </button>
          {topDiagOpen && (
            <div className="mt-1 p-2 rounded border border-[var(--card-border)] bg-[var(--input-bg)]/30">
              <ul className="text-[10px] text-[var(--muted-text)] space-y-1">
                <li>
                  <span className="font-black uppercase tracking-widest mr-1">Generated:</span>
                  {fmtEtTime(payload.generated_at)}
                </li>
                <li>
                  <span className="font-black uppercase tracking-widest mr-1">Files scanned:</span>
                  {payload.diagnostics_raw.files_scanned.length}
                </li>
                {payload.diagnostics_raw.missing_sources.length > 0 && (
                  <li>
                    <span className="font-black uppercase tracking-widest mr-1">Missing:</span>
                    {payload.diagnostics_raw.missing_sources.length}
                  </li>
                )}
                {payload.diagnostics_raw.errors.length > 0 && (
                  <li>
                    <span className="font-black uppercase tracking-widest mr-1">Errors:</span>
                    {payload.diagnostics_raw.errors.length}
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
